<template>
  <main class="main">
    <MonCompteComponent v-if="utilisateur" :utilisateur />

    <h1 class="title title--1">Mes clés d'accès</h1>
    <CleAccesComponent
      v-for="cle in cles"
      :key="cle.id"
      :cle
      :sessions="sessions.filter((session) => session.cle_acces === cle.id)"
      @deleteCle="deleteCle"
      @delete-session="deleteSession"
    />
  </main>
</template>

<script setup lang="ts">
import useFetch from '@/composables/useFetch.ts'
import { onMounted, ref, type Ref } from 'vue'
import type { UtilisateurGet } from '@/interfaces/Utilisateur.ts'
import type { SessionList } from '@/interfaces/Session.ts'
import type { CleAccesList } from '@/interfaces/CleAcces.ts'
import CleAccesComponent from '@/components/mon-compte/CleAccesComponent.vue'
import MonCompteComponent from '@/components/mon-compte/MonCompteComponent.vue'
import { useToastStore } from '@/stores/toast.ts'
import { useLoadingStore } from '@/stores/loading.ts'
import promiseWithTimeout from '@/mixins/promiseWithTimeout'
import { useCacheStore } from '@/stores/cache.ts'
import { useUserStore } from '@/stores/user.ts'

// dépendances
const api = useFetch()

// stores
const cacheStore = useCacheStore()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()
const userStore = useUserStore()

// données
const utilisateur: Ref<UtilisateurGet | null> = ref(null)
const sessions: Ref<SessionList[]> = ref([])
const cles: Ref<CleAccesList[]> = ref([])

// fonctions
async function getUtilisateur() {
  utilisateur.value = await userStore.getUtilisateur()
}

// sessions
async function getSessions() {
  sessions.value = await cacheStore.getSessions()
}
async function deleteSession(sessionId: string) {
  loadingStore.start()
  try {
    await api.delete(`sessions/${sessionId}`)
    toastStore.success('La session a bien été supprimée.')
    await getSessions()
  } finally {
    loadingStore.stop()
  }
}

// clés
async function getCles() {
  cles.value = await cacheStore.getCles()
}
async function deleteCle(cleId: string) {
  loadingStore.start()
  try {
    const credentialId = await api.delete(`cle-acces/${cleId}`)
    // Vérifie la compatibilité navigateur
    // @ts-expect-error: signalUnknownCredential n'est pas encore dans les types TypeScript
    const signalFn = PublicKeyCredential.signalUnknownCredential
    if (signalFn) {
      // Détermine dynamiquement le RP ID
      const rpId = import.meta.env.VITE_RELYING_PARTY_ID || window.location.hostname
      // On encapsule l'appel dans un timeout sécurisé
      await promiseWithTimeout(
        signalFn({
          rpId,
          credentialId, // base64url encoded credential ID
        }),
        800, // timeout (ms)
        "signalUnknownCredential n'a pas répondu à temps",
      )
    }
  } catch (error) {
    console.error("Erreur lors de la suppression de la clé d'accès :", error)
  } finally {
    toastStore.success("La clé d'accès a bien été supprimée.")
    await getSessions()
    await getCles()
    loadingStore.stop()
  }
}

onMounted(async () => {
  loadingStore.start()
  try {
    await getUtilisateur()
    await getSessions()
    await getCles()
  } finally {
    loadingStore.stop()
  }
})
</script>
